// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';
import { superpathjoin } from 'superpathjoin';

import {
  selectOneProductErrors,
  selectProductOptionsUIFactory,
  type DecoratedProduct,
  updateProductAndGetCartOrNotify,
} from '@gandi/cart';
import { selectPersonalId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import { Alert } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectAdminURL } from '../../../../../modules/config';
import { useBestCatalogPrice } from '../../modules/catalog';
import { DomainLabel } from '../components/DomainLabel';
import ProductErrorItem from '../components/ProductErrorItem';
import ProductItem from '../components/ProductItem';

import { DurationRow } from '../components/Rows';
import type { GandiFormSelectOption } from '../types';

import MailboxRenewSuggestionRow from './MailboxRenewSuggestionRow';

// eslint-disable-next-line import/no-cycle
import Option from './Option';

import { useMailboxToRenew } from './modules';

type Props = {
  product: DecoratedProduct,
};

export default function DomainRenewRestore({
  product,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const productId = product.id;
  const fqdn = product.identifier || '';

  const { durations, priceCrossedOut, total } = useBestCatalogPrice(productId);

  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, { productId }),
  );
  const selectProductOptionsUI = selectProductOptionsUIFactory();
  const productOptions = useAppSelector(state =>
    selectProductOptionsUI(state, productId),
  );

  // email renewals linked to this domain
  const { mailboxesToRenew, totalCount } = useMailboxToRenew(fqdn);

  const mailboxList: Array<
    ExactReactElement_DEPRECATED<typeof MailboxRenewSuggestionRow | 'a'>,
  > = mailboxesToRenew.map(mailbox => (
    <MailboxRenewSuggestionRow mailbox={mailbox} key={mailbox.login} />
  ));

  const adminUrl = useAppSelector(selectAdminURL);
  const personalOrgaId = useAppSelector(selectPersonalId);
  const mailboxesAdminUrl = superpathjoin(
    adminUrl,
    'domain',
    personalOrgaId,
    fqdn,
    'mail',
    'mailboxes',
  );

  if (totalCount > mailboxesToRenew.length)
    mailboxList.push(
      <a key="link_all_mb" href={mailboxesAdminUrl}>
        {__('View all mailboxes.')}
      </a>,
    );

  const infoLabel = __('This domain has %(count)s mailboxes up for renewal.', {
    count: totalCount,
  });

  // durations
  const handleDurationChange: (
    selectedOption: GandiFormSelectOption<number>,
  ) => void = (selectedOption: GandiFormSelectOption<number>) => {
    const newPayload = {
      ...product.payload,
      duration: selectedOption.value,
    };

    dispatch(updateProductAndGetCartOrNotify(__, product.id, newPayload));
  };

  // render
  const label = <DomainLabel product={product} />;

  if (productErrors.length > 0)
    return (
      <ProductErrorItem
        key={`${product.id}-error-row`}
        label={label}
        productId={product.id}
        errors={productErrors}
      />
    );

  return (
    <ProductItem
      key={`${product.id}-row`}
      hasTaxes={product.hasTaxes}
      priceBeforeTaxes={
        product.isWorking
          ? undefined
          : product.total?.before_taxes || product.unitPrice
      }
      productId={product.id}
      label={label}
    >
      <DurationRow
        value={product.payload.duration || 1}
        values={durations}
        disabledOption={!!product.isWorking}
        onChange={handleDurationChange}
        priceCrossedOut={priceCrossedOut}
        productId={product.id}
        total={total}
      />

      {productOptions.map(option => (
        <Option option={option} key={option.label} />
      ))}
      {mailboxList.length > 0 && (
        <Alert
          intent="info"
          marginBottom="xsmall"
          message={mailboxList}
          title={infoLabel}
          backgroundColor="surface"
          withBackground
        />
      )}
    </ProductItem>
  );
}
